import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, throwError } from 'rxjs';
import { ApiBaseService } from '../api-base-service';
import { Success } from '../responses/api-response';
import { StorageService } from '@portal/shared/services/storage.service';
import {
    AdjustOnHandCommandDto,
    CreateInventoryItemDto,
    InventoryBalanceDto,
    InventoryBalanceQuery,
    InventoryCatalogQuery,
    InventoryItemDto,
    InventoryItemType,
    MerchandisingCheckoutDto,
    MerchandisingDashboardDto,
    MerchandisingRevenueBreakdownDto,
    MerchandisingSaleDto,
    MerchandisingSaleResultDto,
    MerchandisingSalesQuery,
    MerchandisingSalesByProductDto,
    InventoryMovementDto,
    InventoryMovementQuery,
    PartyEventInventoryBalanceDto,
    PartyEventInventoryMutationResultDto,
    PartyEventInventoryStateDto,
    PartyEventInventoryQuantityCommandDto,
    PagedResultDto,
    ReceiveStockCommandDto,
    RegisterMerchandisingSaleDto,
    UpdateInventoryItemDto,
    UpdateMerchandisingSaleDto
} from './inventory.models';

@Injectable({
    providedIn: 'root'
})
export class InventoryApiService extends ApiBaseService {
    constructor(
        public http: HttpClient,
        private readonly _storageService: StorageService
    ) {
        super(http);
    }

    getCatalog(query: InventoryCatalogQuery): Observable<Success<PagedResultDto<InventoryItemDto>>> {
        const scoped = this.withOrganizationScope(query);
        const requestQuery = {
            ...scoped,
            itemType: scoped.type ? this.toApiItemType(scoped.type) : undefined,
            type: undefined
        };

        const basePath = this.resolveInventoryBasePath(scoped.organizationId);
        const queryString = this.buildQuery(this.isOrgInventoryContext() ? {
            ...requestQuery,
            organizationId: undefined
        } : requestQuery);

        return this.get<Success<PagedResultDto<InventoryItemDto>>>(`${basePath}/catalog?${queryString}`);
    }

    getMerchandisingDashboard(organizationId?: string): Observable<Success<MerchandisingDashboardDto>> {
        const basePath = this.resolveMerchandisingBasePath(organizationId);
        const queryString = this.buildQuery(this.isOrgMerchandisingContext() ? {} : this.withOrganizationScope({ organizationId }));
        return this.get<Success<MerchandisingDashboardDto>>(
            `${basePath}/dashboard${queryString ? `?${queryString}` : ''}`
        );
    }

    getMerchandisingCheckout(organizationId?: string): Observable<Success<MerchandisingCheckoutDto>> {
        const basePath = this.resolveMerchandisingBasePath(organizationId);
        const queryString = this.buildQuery(this.isOrgMerchandisingContext() ? {} : this.withOrganizationScope({ organizationId }));
        return this.get<Success<MerchandisingCheckoutDto>>(
            `${basePath}/checkout${queryString ? `?${queryString}` : ''}`
        );
    }

    getMerchandisingRevenueBreakdown(organizationId?: string): Observable<Success<MerchandisingRevenueBreakdownDto>> {
        const basePath = this.resolveMerchandisingBasePath(organizationId);
        const queryString = this.buildQuery(this.isOrgMerchandisingContext() ? {} : this.withOrganizationScope({ organizationId }));
        return this.get<Success<MerchandisingRevenueBreakdownDto>>(
            `${basePath}/revenue-breakdown${queryString ? `?${queryString}` : ''}`
        );
    }

    getMerchandisingSalesByProduct(organizationId?: string): Observable<Success<MerchandisingSalesByProductDto>> {
        const queryString = this.buildQuery(this.isOrgMerchandisingContext() ? {} : this.withOrganizationScope({ organizationId }));
        return this.get<Success<MerchandisingSalesByProductDto>>(
            `inventory/merchandising/sales/by-product${queryString ? `?${queryString}` : ''}`
        );
    }

    registerMerchandisingSale(payload: RegisterMerchandisingSaleDto): Observable<Success<MerchandisingSaleResultDto>> {
        const requestPayload = this.withOrganizationScope(payload);
        const endpoint = this.isOrgMerchandisingContext()
            ? `${this.resolveMerchandisingBasePath(requestPayload.organizationId)}/sales`
            : 'inventory/merchandising/sales';

        return this.post<Success<MerchandisingSaleResultDto>, RegisterMerchandisingSaleDto>(
            requestPayload,
            endpoint
        );
    }

    getMerchandisingSales(query: MerchandisingSalesQuery): Observable<Success<PagedResultDto<MerchandisingSaleDto>>> {
        const scoped = this.withOrganizationScope(query);
        const basePath = this.resolveMerchandisingBasePath(scoped.organizationId);
        const queryString = this.buildQuery(this.isOrgMerchandisingContext() ? {
            ...scoped,
            organizationId: undefined
        } : scoped);

        return this.get<Success<PagedResultDto<MerchandisingSaleDto>>>(
            `${basePath}/sales?${queryString}`
        );
    }

    cancelMerchandisingSale(saleId: string, organizationId?: string): Observable<Success<boolean>> {
        const scoped = this.withOrganizationScope({ organizationId });
        const isOrgContext = this.isOrgMerchandisingContext();
        const basePath = this.resolveMerchandisingBasePath(scoped.organizationId);
        const query = this.buildQuery(isOrgContext ? {} : scoped);
        const querySuffix = query ? `?${query}` : '';
        return this.deleteBase<Success<boolean>>(`${basePath}/sales/${saleId}${querySuffix}`);
    }

    updateMerchandisingSale(saleId: string, payload: UpdateMerchandisingSaleDto): Observable<Success<MerchandisingSaleResultDto>> {
        const requestPayload = this.withOrganizationScope(payload);
        const endpoint = this.isOrgMerchandisingContext()
            ? `${this.resolveMerchandisingBasePath(requestPayload.organizationId)}/sales/${saleId}`
            : `inventory/merchandising/sales/${saleId}`;
        return this.put<Success<MerchandisingSaleResultDto>, UpdateMerchandisingSaleDto>(requestPayload, endpoint);
    }

    createCatalogItem(payload: CreateInventoryItemDto): Observable<Success<InventoryItemDto>> {
        const requestPayload = this.withOrganizationScope({
            ...payload,
            itemType: this.toApiItemType(payload.itemType)
        });

        return this.post<Success<InventoryItemDto>, any>(requestPayload, 'inventory/items').pipe(
            catchError((error) => {
                if (error?.status === 404 || error?.status === 405) {
                    return this.post<Success<InventoryItemDto>, any>(requestPayload, 'inventory/catalog');
                }
                return throwError(() => error);
            })
        );
    }

    updateCatalogItem(itemId: string, payload: UpdateInventoryItemDto): Observable<Success<InventoryItemDto>> {
        const requestPayload = this.withOrganizationScope({
            ...payload,
            itemType: this.toApiItemType(payload.itemType)
        });

        return this.put<Success<InventoryItemDto>, any>(requestPayload, `inventory/items/${itemId}`);
    }

    deleteCatalogItem(itemId: string, organizationId?: string): Observable<Success<boolean>> {
        const query = this.buildQuery(this.withOrganizationScope({ organizationId }));
        const querySuffix = query ? `?${query}` : '';
        return this.deleteBase<Success<boolean>>(`inventory/items/${itemId}${querySuffix}`);
    }

    getBalances(query: InventoryBalanceQuery): Observable<Success<PagedResultDto<InventoryBalanceDto>>> {
        const scoped = this.withOrganizationScope(query);
        const requestQuery = {
            ...scoped,
            itemType: scoped.type ? this.toApiItemType(scoped.type) : undefined,
            type: undefined
        };

        const basePath = this.resolveInventoryBasePath(scoped.organizationId);
        const queryString = this.buildQuery(this.isOrgInventoryContext() ? {
            ...requestQuery,
            organizationId: undefined
        } : requestQuery);

        return this.get<Success<PagedResultDto<InventoryBalanceDto>>>(`${basePath}/balances?${queryString}`);
    }

    getMovements(query: InventoryMovementQuery): Observable<Success<PagedResultDto<InventoryMovementDto>>> {
        return this.get<Success<PagedResultDto<InventoryMovementDto>>>(`inventory/movements?${this.buildQuery(this.withOrganizationScope(query))}`);
    }

    getPartyEventBalances(
        partyEventId: string,
        query: InventoryBalanceQuery
    ): Observable<Success<PagedResultDto<PartyEventInventoryBalanceDto>>> {
        return this.get<Success<PagedResultDto<PartyEventInventoryBalanceDto>>>(
            `party-events/${partyEventId}/inventory/balances?${this.buildQuery(this.withOrganizationScope(query))}`
        );
    }

    getPartyEventState(
        partyEventId: string,
        organizationId?: string
    ): Observable<Success<PartyEventInventoryStateDto>> {
        return this.get<Success<PartyEventInventoryStateDto>>(
            `party-events/${partyEventId}/inventory/state?${this.buildQuery(this.withOrganizationScope({ organizationId }))}`
        );
    }

    receive(payload: ReceiveStockCommandDto): Observable<Success<InventoryBalanceDto>> {
        return this.post<Success<InventoryBalanceDto>, ReceiveStockCommandDto>(this.withOrganizationScope(payload), 'inventory/receive');
    }

    adjust(payload: AdjustOnHandCommandDto): Observable<Success<InventoryBalanceDto>> {
        return this.post<Success<InventoryBalanceDto>, AdjustOnHandCommandDto>(this.withOrganizationScope(payload), 'inventory/adjust');
    }

    allocate(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/allocate`
        );
    }

    consume(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/consume`
        );
    }

    returnFromEvent(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/return`
        );
    }

    loss(partyEventId: string, payload: PartyEventInventoryQuantityCommandDto): Observable<Success<PartyEventInventoryMutationResultDto>> {
        return this.post<Success<PartyEventInventoryMutationResultDto>, PartyEventInventoryQuantityCommandDto>(
            this.withOrganizationScope(payload),
            `party-events/${partyEventId}/inventory/loss`
        );
    }

    reconcile(
        partyEventId: string,
        payload?: { reason?: string; organizationId?: string }
    ): Observable<Success<PartyEventInventoryMutationResultDto[]>> {
        return this.post<Success<PartyEventInventoryMutationResultDto[]>, { reason?: string; organizationId?: string }>(
            this.withOrganizationScope(payload ?? {}),
            `party-events/${partyEventId}/inventory/reconcile`
        );
    }

    reopenPartyEventInventory(
        partyEventId: string,
        organizationId?: string
    ): Observable<Success<PartyEventInventoryStateDto>> {
        return this.post<Success<PartyEventInventoryStateDto>, { organizationId?: string }>(
            this.withOrganizationScope({ organizationId }),
            `party-events/${partyEventId}/inventory/reopen`
        );
    }

    private buildQuery<T extends object>(params: T): string {
        const entries = Object.entries(params as Record<string, unknown>)
            .filter(([, value]) => value !== undefined && value !== null && value !== '');

        return entries
            .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
            .join('&');
    }

    private withOrganizationScope<T extends { organizationId?: string }>(payload: T): T {
        const organizationId =
            this.coerceToString(payload.organizationId) ||
            this.coerceToString(this._storageService.getOrg()?.id as unknown) ||
            this.tryGetOrgIdFromCurrentUrl();
        if (!organizationId) {
            return payload;
        }

        return {
            ...payload,
            organizationId
        };
    }

    private coerceToString(value: unknown): string | undefined {
        if (value === undefined || value === null) {
            return undefined;
        }

        const asString = String(value).trim();
        return asString.length > 0 ? asString : undefined;
    }

    private tryGetOrgIdFromCurrentUrl(): string | undefined {
        if (typeof window === 'undefined') {
            return undefined;
        }

        const match = window.location.pathname.match(/\/org\/([0-9a-fA-F-]{36})(?:\/|$)/i);
        return match?.[1];
    }

    private isOrgInventoryContext(): boolean {
        return this.getOrgScopedInventoryRoute()?.section === 'inventory'
            || this.getOrgScopedInventoryRoute()?.section === 'merchandising';
    }

    private isOrgMerchandisingContext(): boolean {
        return this.getOrgScopedInventoryRoute()?.section === 'merchandising';
    }

    private resolveInventoryBasePath(organizationId?: string): string {
        const scopedOrgId = this.coerceToString(organizationId) || this.getOrgScopedInventoryRoute()?.organizationId;
        return this.isOrgInventoryContext() && scopedOrgId
            ? `orgs/${scopedOrgId}/inventory`
            : 'inventory';
    }

    private resolveMerchandisingBasePath(organizationId?: string): string {
        const scopedOrgId = this.coerceToString(organizationId) || this.getOrgScopedInventoryRoute()?.organizationId;
        return this.isOrgMerchandisingContext() && scopedOrgId
            ? `orgs/${scopedOrgId}/merchandising`
            : 'inventory/merchandising';
    }

    private getOrgScopedInventoryRoute(): { organizationId: string; section: 'inventory' | 'merchandising' } | null {
        if (typeof window === 'undefined') {
            return null;
        }

        const match = window.location.pathname.match(/\/org\/([0-9a-fA-F-]{36})\/(inventory|merchandising)(?:\/|$)/i);
        if (!match) {
            return null;
        }

        return {
            organizationId: match[1],
            section: match[2].toLowerCase() as 'inventory' | 'merchandising'
        };
    }

    private toApiItemType(itemType: InventoryItemType | number | string): number {
        if (typeof itemType === 'number') {
            return itemType;
        }

        const map: Record<string, number> = {
            Asset: 1,
            Consumable: 2,
            Merchandise: 3
        };

        const mapped = map[String(itemType)];
        if (mapped !== undefined) {
            return mapped;
        }

        const asNumber = Number(itemType);
        return Number.isFinite(asNumber) ? asNumber : 0;
    }
}
